import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Pagination, 
  Box, 
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { getBlogPosts } from '../services/blogService';
import { useAuth } from '../context/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';
import BlogDialog from '../components/BlogDialog';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const { isAuthenticated, user } = useAuth();

  // Blog Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'view', 'edit'
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const data = await getBlogPosts(page, 6, selectedTag);
        setPosts(data.posts);
        setTotalPages(data.totalPages);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchPosts();
  }, [page, selectedTag]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? null : tag);
    setPage(1);
  };

  const handleCreatePost = () => {
    setDialogMode('create');
    setSelectedPost(null);
    setDialogOpen(true);
  };

  const handleViewPost = (post) => {
    setDialogMode('view');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setDialogMode('edit');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPost(null);
  };

  const handleDialogSuccess = async () => {
    // Refresh posts after create/edit
    try {
      const data = await getBlogPosts(page, 6, selectedTag);
      setPosts(data.posts);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Error refreshing posts:', err);
    }
  };

  // Extract all unique tags from posts
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  const blogActions = isAuthenticated ? (
    <Button 
      onClick={handleCreatePost}
      variant="contained" 
      startIcon={<AddIcon />}
    >
      New Post
    </Button>
  ) : null;

  return (
    <DashboardLayout title="Blog" actions={blogActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>
      
      {/* Tags filter */}
      {allTags.length > 0 && (
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {allTags.map(tag => (
            <Chip 
              key={tag} 
              label={tag}
              onClick={() => handleTagClick(tag)}
              color={selectedTag === tag ? 'primary' : 'default'}
              clickable
            />
          ))}
          {selectedTag && (
            <Button 
              size="small" 
              onClick={() => setSelectedTag(null)} 
              sx={{ ml: 1 }}
            >
              Clear filter
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6">
            {selectedTag 
              ? `No posts found with tag "${selectedTag}"`
              : 'No blog posts yet'}
          </Typography>
          {isAuthenticated && (
            <Button 
              onClick={handleCreatePost}
              variant="contained" 
              sx={{ mt: 2 }}
            >
              Create the first post
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {posts.map(post => (
              <Grid item xs={12} sm={6} md={4} key={post._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  {post.featuredImage && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={post.featuredImage}
                      alt={post.title}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2" noWrap>
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        mb: 2
                      }}
                    >
                      {post.content.replace(/<[^>]*>/g, '').slice(0, 150)}...
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {post.createdAt && format(new Date(post.createdAt), 'MMM d, yyyy')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          onClick={() => handleViewPost(post)}
                          size="small"
                          startIcon={<VisibilityIcon />}
                        >
                          View
                        </Button>
                        {isAuthenticated && post.author?._id === user?.id && (
                          <Button 
                            onClick={() => handleEditPost(post)}
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                          >
                            Edit
                          </Button>
                        )}
                      </Box>
                    </Box>
                    
                    {post.tags && post.tags.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {post.tags.map(tag => (
                          <Chip 
                            key={tag} 
                            label={tag} 
                            size="small" 
                            onClick={(e) => {
                              e.preventDefault();
                              handleTagClick(tag);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Blog Dialog - Advanced Popup */}
      <BlogDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        mode={dialogMode}
        blogPost={selectedPost}
        onSuccess={handleDialogSuccess}
      />
    </Box>
    </DashboardLayout>
  );
};

export default Blog; 